import {Session} from "next-auth";
import {request} from "./httpInterceptor";
import {getLocalStorageItem} from "./localStorageUtil";
import {ILoggedOutShoppingList} from "../interfaces/ILoggedOutShoppingList";
import {AddItemType} from "../types/AddItemType";

const showLog = false;

const fetchShowChat = async (countryLang: string | null, session: Session | null) => {
  const res = await request({
    url: `/${countryLang}/showChat`,
    method: 'post',
  });

  return res.data;
}

enum Source {
  ADVISOR,
  ADVISOR_AUTOMATED,//This is used for example to send automatic answers when a chat is not handled for a period of time
  CUSTOMER
}

enum ChatEvent {
  CHAT_ACCEPTED,
  CHAT_MESSAGE,
  CHAT_CLOSED
}

export type ChatMessage = {
  uuid: string,
  message: string,
  messageTime: string,
  source: Source,
  invocaChatId: number,
  chatMessageId: number,
  readTime: string,
  handlerUser: string,
  chatEvent: ChatEvent
}

const sessionChatKey = 'chatMessageIds'

function storeChatMessagesInSession(chatMessages: ChatMessage[] | null) {
  showLog && console.log("sessionChatKey: " + sessionChatKey + " | Messages: " + JSON.stringify(chatMessages));
  sessionStorage.setItem(sessionChatKey, JSON.stringify(chatMessages));
}

function isChatInitiated() : boolean {
  let item = sessionStorage.getItem("chatInit");
  return item != null && item == 'true';
}

function setIsChatInitiated() {
  sessionStorage.setItem("chatInit", "true")
}

class ChatManager {
  private static instance: ChatManager;
  private readonly countryLang: string;
  private readonly session: any;

  //Defined values
  private showChat: boolean | undefined = undefined;
  private chatClosedCallback: any;
  private messageFromOperatorCallback: any;
  private messageFromVisitorErrorCallback: any;

  private constructor(countryLang: string, session: any, status: any) {
    this.countryLang = countryLang;
    this.session = session;
  }

  private isChatMessageInSession(uuid: string) {
    const chatMessages = this.getChatMessagesFromSession();
    return chatMessages && chatMessages.some(chatMessage => chatMessage.uuid === uuid);
  }

  private addChatMessageToSession(chatMessage: ChatMessage) {
    showLog && console.log("addChatMessageToSession: " + JSON.stringify(chatMessage))
    let chatMessages: ChatMessage[] | null = this.getChatMessagesFromSession();
    if (chatMessages) {
      chatMessages.push(chatMessage)
    }
    else {
      chatMessages = [chatMessage];
    }

    storeChatMessagesInSession(chatMessages);
  }

  public getChatMessagesFromSession() : ChatMessage[] | null {
    const chatMessagesJson = sessionStorage.getItem(sessionChatKey);
    if (chatMessagesJson) {
      return JSON.parse(chatMessagesJson);
    }

    return null;
  }

  public static getInstance(countryLang: string, session: any, status: any): ChatManager {
    if (!this.instance) {
      this.instance = new ChatManager(countryLang, session, status);
      this.instance.start();
    }

    return this.instance;
  }

  public setMessageFromOperatorCallback(messageFromOperatorCallback: any) {
    this.messageFromOperatorCallback = messageFromOperatorCallback;
  }

  public setChatClosedCallback(chatClosedCallback: any) {
    this.chatClosedCallback = chatClosedCallback;
  }

  public setMessageFromVisitorErrorCallback(messageFromVisitorErrorCallback: any) {
    this.messageFromVisitorErrorCallback = messageFromVisitorErrorCallback;
  }

  public async shouldShowChat() {
    if (this.showChat !== undefined) {
      showLog && console.log("Already calculated: " + this.showChat)
      return this.showChat;
    }

    const showChatQuery = await fetchShowChat(this.countryLang, this.session);
    showLog && console.log("showChatQuery: " + JSON.stringify(showChatQuery) + ". Res: " + showChatQuery?.hasAdvisors);
    this.showChat = showChatQuery?.hasAdvisors;
    return this.showChat;
  }

  private getCart(): AddItemType[] {
    let cart: AddItemType[] = [];
    const persistedCart = getLocalStorageItem("loggedOutCart");
    if (persistedCart != null) {
      JSON.parse(persistedCart).map((item: ILoggedOutShoppingList) => {
        const transformed = {
          productId: item.product.productID,
          quantity: item.quantity
        }
        cart.push(transformed)
      })
    }

    return cart;
  }

  //Send message back to server->Invoca
  async onMessageFromVisitor(message: string, host: string | undefined = undefined) {
    showLog && console.log("Chat manager: " + message);
    if (!isChatInitiated()) {
      setIsChatInitiated();
    }

    if (message === undefined) {
      showLog && console.log("message is undefined");
      return;
    }

    const body = {
      message: message,
      ip: null,
      host: host,
      products: this.getCart()
    }

    const res = await request({
      url: `/${this.countryLang}/onMessageFromVisitor`,
      data: body,
      method: 'post',
      session: this.session
    });

    if (!res.data.success && this.messageFromVisitorErrorCallback) {
      this.messageFromVisitorErrorCallback()
    }
    else {
      showLog && console.log("onMessageFromVisitor data: " + JSON.stringify(res.data));

      const now = new Date();
      this.addChatMessageToSession({
        uuid: "",
        message: message,
        source: Source.CUSTOMER,
        messageTime: now.toUTCString(),
        invocaChatId: res.data.websiteChatMessage.invocaChatId,
        chatMessageId: res.data.websiteChatMessage.chatMessageId,
        readTime: res.data.websiteChatMessage.readTime,
        handlerUser: res.data.websiteChatMessage.handlerUser,
        chatEvent: ChatEvent.CHAT_MESSAGE,
      });
    }
  }

  private async fetchMessages() {
    try {
      const res = await request({
        url: `/${this.countryLang}/getChatMessages`,
        method: 'get',
        session: this.session
      })

      return res.data;
    } catch (e) {
      //TODO
      //Handle fetchErrors
    }

    return null;
  }

  private notifyMessageRead(chatMessage: ChatMessage) {
    request({
      url: `/${this.countryLang}/onChatMessageRead`,
      data: {
        uuid: chatMessage.uuid,
        messageTime: chatMessage.messageTime,
        invocaChatId: chatMessage.invocaChatId,
        chatMessageId: chatMessage.chatMessageId
      },
      method: 'post',
      session: this.session
    })
  }

  private async handleNewMessages(showChat: boolean | undefined) {
    showLog && console.log("handleNewMessages: " + showChat)
    if (!showChat) {
      return;
    }

    if (!isChatInitiated()) {
      return;
    }

    const data = await this.fetchMessages();
    if (!data) {
      return
    }

    data.shopMessages.forEach((chatMessage: ChatMessage) => {
      if (!this.isChatMessageInSession(chatMessage.uuid)) {
        showLog && console.log("New chat message: " + JSON.stringify(chatMessage));
        //Storage message in session
        if (!chatMessage.source) {
          chatMessage.source = Source.ADVISOR;
        }

        if (chatMessage.chatEvent && chatMessage.chatEvent.toString() === ChatEvent[ChatEvent.CHAT_CLOSED]) {
          if (this.chatClosedCallback) {
            this.chatClosedCallback();
          }
        }
        else {
          this.addChatMessageToSession(chatMessage);

          if (this.messageFromOperatorCallback) {
            this.messageFromOperatorCallback(chatMessage);
          }
        }

        this.notifyMessageRead(chatMessage);
      } else {
        showLog && console.log("Already in session: " + JSON.stringify(chatMessage));
      }
    })
  }

  private start() {
    setInterval(() => this.handleNewMessages(this.showChat), 4000)
  }
}

export default ChatManager;